import React from 'react'
import PropTypes from 'prop-types'

import { navigate } from 'gatsby'
import {
  useUserService,
  hasDOM,
  ErrorBoundary,
  isInternic,
  DomainDashboard,
} from '@rebeldotcom/ui'
import { Box, Stack } from '@rebeldotcom/components'
import { VerticalMenu } from './layout/vertical-list-menu'
import { domainStrings } from './my-account/dashboard/domainStrings'

const manageAccountMenu = {
  heading: 'My Account',
  items: [
    {
      id: 'account-settings',
      label: 'Account Settings',
      url: '/my-account/user-details',
    },
    {
      id: 'payment-settings',
      label: 'Payment Settings',
      url: '/my-account/settings/payment',
    },
    {
      id: 'billing-history',
      label: 'Billing History',
      url: '/my-account/billing-history',
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      url: '/MyAccount/Subscriptions.aspx',
    },
    {
      id: 'login-history',
      label: 'Login History',
      url: '/my-account/login-history',
    },
    {
      id: 'security-settings',
      label: 'Security Settings',
      url: '/my-account/settings/security',
    },
  ],
}
const manageDomainsMenu = {
  heading: 'My Domains',
  items: [
    {
      id: 'domain-manager',
      label: 'Domain Manager',
      url: '/MyAccount/Portfolio.aspx',
    },
    {
      id: 'transfer-manager',
      label: 'Transfer Manager',
      url: '/MyAccount/TransferManager.aspx',
    },
    {
      id: 'contact-manager',
      label: 'Contact Manager',
      url: '/contacts/manage/',
    },
  ],
}
const manageHostingMenu = {
  heading: 'My Hosting',
  items: [
    {
      id: 'hosting-manager',
      label: 'Hosting Manager',
      url: '/MyAccount/HostingPackages.aspx',
    },
    {
      id: 'email-manager',
      label: 'Cloud Email Manager',
      url: '/my-account/email-manager',
    },
    {
      id: 'google-workspace-manager',
      label: 'Google Workspace Manager',
      url: '/my-account/google-workspace',
    },
    {
      id: 'seo-manager',
      label: 'SEO Manager',
      url: '/my-account/seo-manager',
    },
    {
      id: 'weebly-website-manager',
      label: 'Weebly Website Manager',
      url: '/Websites/Manage',
    },
    {
      id: 'website-security-manager',
      label: 'Website Security Manager',
      url: '/MyAccount/SecurityManager.aspx',
    },
    {
      id: 'ssl-manager',
      label: 'SSL Manager',
      url: '/MyAccount/SSLManager.aspx',
    },
    {
      id: 'classicwebmail',
      label: 'Classic Webmail',
      url: isInternic
        ? 'https://webmail.internic.ca'
        : 'https://webmail.rebel.com',
    },
    {
      id: 'cloudwebmail',
      label: 'Cloud Webmail',
      url: isInternic ? 'https://mail.internic.ca' : 'https://mail.rebel.com',
    },
  ],
}
const menuStyle = {
  headingVariant: 'gigaBold',
  itemVariant: 'milli',
}

const propTypes = {
  component: PropTypes.elementType,
  strings: PropTypes.object,
}

const defaultProps = {
  component: DomainDashboard,
  strings: domainStrings,
}

/**
 * This is the sum of the navbar height ( spaces[5] = 6.4rem)
 * and the vertial menu stack (space[4] = 3.2rem)
 */
const stickyTop = '9.6rem'

const AccountPage = ({ component: Component, strings, ...rest }) => {
  const { user, isLoading } = useUserService()
  if (hasDOM && (user || !isLoading)) {
    if (user && user.customerId) {
      if (user.status === 'On Hold') {
        navigate('/OnHold.aspx')
        return null
      }
      if (
        user.status === 'CreditCardOnHold' &&
        !window.location.toString().includes('/my-account/settings/payment')
      ) {
        navigate('/my-account/settings/payment')

        return null
      }

      return (
        <>
          <ErrorBoundary location="/my-account/">
            <Box width="100%">
              <Stack
                alignSelf="start"
                bg="white"
                display={['none', 'flex']}
                flexGrow={1}
                gridGap="2"
                m={4}
                position="sticky"
                top={stickyTop}
                width="20rem"
              >
                <VerticalMenu menu={manageDomainsMenu} menuStyle={menuStyle} />
                <VerticalMenu menu={manageHostingMenu} menuStyle={menuStyle} />
                <VerticalMenu menu={manageAccountMenu} menuStyle={menuStyle} />
              </Stack>
              <Box bg="whiteDark" padding={[2, 3, 4]} width="100%">
                <Stack width="100%">
                  <Component strings={strings} {...rest} />
                </Stack>
              </Box>
            </Box>
          </ErrorBoundary>
        </>
      )
    }

    navigate(
      `/account?ReturnUrl=${window.location.pathname}${window.location.hash}`
    )
  }

  return null
}

AccountPage.propTypes = propTypes
AccountPage.defaultProps = defaultProps

export default AccountPage
